import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatMoney } from '../lib/format'
import { canEnterData } from '../lib/types'
import { leaseTouchesYear } from '../lib/rules'
import { useSelectedYear } from '../year/useYear'
import type { Unit, UnitKind } from '../lib/types'
import { Loading, ErrorState, EmptyState, Banner } from '../components/States'
import { Occupancy } from '../components/Occupancy'
import { UnitSheet } from './UnitSheet'

interface UnitRow extends Unit {
  /** Bail concernant l'annee consultee, s'il y en a un. */
  lease: {
    id: string
    tenant_id: string
    rent_ttc: number
    start_date: string | null
    end_date: string | null
    status: string
    tenant: { full_name: string } | null
  } | null
  /**
   * Date de debut de la PLUS ANCIENNE occupation de cette unite par ce
   * locataire, tous baux successifs confondus.
   */
  occupiedSince: string | null
}

/**
 * Les neuf unites, chacune avec le bail qui concerne L'ANNEE
 * CONSULTEE. Ouvrir 2025 montre donc les locataires de 2025, pas ceux
 * d'aujourd'hui.
 */
async function loadUnits(year: number): Promise<UnitRow[]> {
  const { data: units, error } = await supabase
    .from('units')
    .select('id, name, kind, floor, notes, vacant_since, vacancy_reason')
    .order('kind', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw error

  // Tous les baux en une seule requete plutot qu'une par unite : neuf
  // allers-retours sur la connexion de Mbour seraient inutilement
  // lents. Le tri par annee est fait ici.
  const { data: leases, error: lErr } = await supabase
    .from('leases')
    .select(
      'id, unit_id, tenant_id, rent_ttc, start_date, end_date, status,' +
        ' tenant:tenants(full_name)',
    )
  if (lErr) throw lErr

  type L = NonNullable<UnitRow['lease']> & { unit_id: string }
  const parUnite = new Map<string, L>()

  for (const l of (leases ?? []) as unknown[]) {
    const lease = l as L
    if (!leaseTouchesYear(lease, year)) continue

    const existing = parUnite.get(lease.unit_id)
    if (!existing) {
      parUnite.set(lease.unit_id, lease)
      continue
    }
    // A egalite, on garde le plus pertinent : un bail actif prime sur
    // un bail expire, qui prime sur un bail termine ; et a statut egal,
    // le plus recent.
    const rang = (s: string) => (s === 'actif' ? 3 : s === 'expire' ? 2 : 1)
    const mieux =
      rang(lease.status) > rang(existing.status) ||
      (rang(lease.status) === rang(existing.status) &&
        (lease.start_date ?? '') > (existing.start_date ?? ''))
    if (mieux) parUnite.set(lease.unit_id, lease)
  }

  // Depuis quand ce locataire occupe cette unite.
  //
  // On prend la PLUS ANCIENNE date de debut parmi ses baux sur cette
  // unite, et non celle du bail en cours : un changement de loyer cree
  // un nouveau bail, et se fier au dernier ferait repartir la duree a
  // zero a chaque augmentation. Un locataire de 2024 dont le loyer a
  // ete revu en 2026 occupe bien l'unite depuis 2024.
  const depuis = new Map<string, string>()
  for (const l of (leases ?? []) as unknown[]) {
    const lease = l as { unit_id: string; tenant_id: string; start_date: string | null }
    if (!lease.start_date) continue
    const cle = `${lease.unit_id}|${lease.tenant_id}`
    const actuel = depuis.get(cle)
    if (!actuel || lease.start_date < actuel) depuis.set(cle, lease.start_date)
  }

  return ((units ?? []) as Unit[]).map((u) => {
    const bail = parUnite.get(u.id) ?? null
    return {
      ...u,
      lease: bail,
      occupiedSince: bail
        ? depuis.get(`${u.id}|${bail.tenant_id}`) ?? bail.start_date
        : null,
    }
  })
}

export function UnitsScreen({ onOpenUnit }: { onOpenUnit: (id: string) => void }) {
  const { t } = useI18n()
  const { profile } = useAuth()
  const { year } = useSelectedYear()
  const { data, loading, error, reload } = useAsync(() => loadUnits(year), [year])
  const [sheet, setSheet] = useState<'closed' | 'new'>('closed')

  const peutSaisir = canEnterData(profile?.role ?? null)

  if (loading) {
    return (
      <div className="screen">
        <h1 className="screen__title">{t('units_title')}</h1>
        <Loading />
      </div>
    )
  }

  if (error) {
    return (
      <div className="screen">
        <h1 className="screen__title">{t('units_title')}</h1>
        <ErrorState message={error} onRetry={reload} />
      </div>
    )
  }

  const units = data ?? []
  const today = new Date()

  const expires = units.filter(
    (u) => u.lease?.end_date && new Date(u.lease.end_date) < today,
  )
  const bientot = units.filter((u) => {
    if (!u.lease?.end_date) return false
    const fin = new Date(u.lease.end_date)
    const limite = new Date(today)
    limite.setDate(limite.getDate() + 60)
    return fin >= today && fin <= limite
  })

  return (
    <div className="screen">
      <div className="screen__head">
        <h1 className="screen__title">{t('units_title')}</h1>
        {peutSaisir && (
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={() => setSheet('new')}
          >
            {t('add')}
          </button>
        )}
      </div>

      {/* La banniere la plus importante de l'application : un bail
          expire est un loyer sans base contractuelle. */}
      {expires.length > 0 && (
        <Banner tone="error">
          <strong>
            {expires.length === 1
              ? t('leases_expired_banner', { n: expires.length })
              : t('leases_expired_banner_plural', { n: expires.length })}
          </strong>
          <span>{expires.map((u) => u.name).join(' · ')}</span>
        </Banner>
      )}

      {bientot.length > 0 && (
        <Banner tone="warn">
          <strong>
            {bientot.length === 1
              ? t('leases_expiring_soon', { n: bientot.length })
              : t('leases_expiring_soon_plural', { n: bientot.length })}
          </strong>
          <span>{bientot.map((u) => u.name).join(' · ')}</span>
        </Banner>
      )}

      {units.length === 0 ? (
        <EmptyState title={t('units_empty_title')} body={t('units_empty_body')} />
      ) : (
        <>
          <p className="muted">{t('units_count', { n: units.length })}</p>
          <ul className="list">
            {units.map((u) => {
              const vacant = u.vacant_since !== null || u.lease === null
              const fin = u.lease?.end_date ? new Date(u.lease.end_date) : null
              const expire = fin !== null && fin < today
              const jours = fin
                ? Math.round((fin.getTime() - today.getTime()) / 86400000)
                : null

              return (
                <li key={u.id}>
                  <button
                    type="button"
                    className="list__item list__item--tappable"
                    onClick={() => onOpenUnit(u.id)}
                  >
                    <span className="list__main">
                      {/* Nom issu de la base, jamais traduit. */}
                      <span className="list__name">{u.name}</span>
                      <span className="list__meta">
                        {u.kind === 'commercial'
                          ? t('unit_kind_commercial')
                          : t('unit_kind_habitation')}
                        {u.floor ? ` · ${u.floor}` : ''}
                      </span>
                      {u.lease?.tenant && (
                        <span className="list__note">{u.lease.tenant.full_name}</span>
                      )}
                      {/* Depuis combien de temps ce locataire occupe
                          cette unite, tous baux successifs confondus. */}
                      {u.lease && (
                        <Occupancy
                          startDate={u.occupiedSince}
                          endDate={u.lease.end_date}
                        />
                      )}
                      {vacant && u.vacant_since && (
                        <span className="list__note">
                          {t('unit_vacant_since', { date: formatDate(u.vacant_since) })}
                        </span>
                      )}
                    </span>

                    <span className="list__side">
                      {u.lease ? (
                        <>
                          <span className="list__amount">
                            {formatMoney(u.lease.rent_ttc)}
                          </span>
                          <span
                            className={`chip ${expire ? 'chip--error' : 'chip--ok'}`}
                          >
                            {expire
                              ? t('lease_expired_since', { n: Math.abs(jours ?? 0) })
                              : t('unit_occupied')}
                          </span>
                        </>
                      ) : (
                        <span className="chip chip--warn">{t('unit_vacant')}</span>
                      )}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </>
      )}

      {sheet === 'new' && (
        <UnitSheet
          unit={null}
          onClose={() => setSheet('closed')}
          onSaved={() => {
            setSheet('closed')
            reload()
          }}
        />
      )}
    </div>
  )
}

export type { UnitKind }
